import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import GradientStrip from "@/components/GradientStrip";
import SEO, { articleJsonLd } from "@/components/SEO";
import { api } from "@/lib/config";
import { useT, useLanguage } from "@/lib/i18n";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function BlogPost() {
  const t = useT();
  const { lang } = useLanguage();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/${slug}`)
      .then(({ data }) => { setPost(data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  if (loading) return <Layout headerVariant="dark"><div className="pt-40 px-6 text-ink/60">{t("common.loading")}</div></Layout>;
  if (notFound || !post) return (
    <Layout headerVariant="dark">
      <div className="pt-40 px-6 max-w-3xl mx-auto text-center py-32">
        <p className="display text-3xl">{t("blog.notFound")}</p>
        <Link to="/blog" className="btn-pill btn-ghost mt-8 inline-flex"><ArrowLeft size={16}/> {t("blog.backToBlog")}</Link>
      </div>
    </Layout>
  );

  return (
    <Layout headerVariant="dark">
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.cover_image || undefined}
        jsonLd={articleJsonLd({
          title: post.title,
          description: post.excerpt,
          slug: post.slug,
          datePublished: post.published_at,
          dateModified: post.updated_at || post.published_at,
          image: post.cover_image,
        })}
      />
      <article data-testid={`blog-post-${post.slug}`}>
        <section className="bg-brand-deep text-white pt-40 md:pt-48 pb-24">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <Link to="/blog" className="editorial-link text-sm uppercase tracking-wider text-white/85 inline-flex items-center gap-2 mb-12">
              <ArrowLeft size={14}/> {t("blog.allArticles")}
            </Link>
            <div className="flex items-center gap-3 mb-6">
              {(post.tags || []).map(tag => (
                <span key={tag} className="text-[11px] uppercase tracking-wider text-accent-yellow border border-accent-yellow/40 px-2.5 py-1">{tag}</span>
              ))}
            </div>
            <h1 className="display text-4xl md:text-6xl leading-[1.05] max-w-4xl">{post.title}</h1>
            <p className="text-white/90 text-lg leading-relaxed mt-8 max-w-3xl">{post.excerpt}</p>
            <p className="text-white/85 text-sm mt-10">
              {post.author} · {new Date(post.published_at).toLocaleDateString(lang === "en" ? "en-US" : "fr-FR", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>
        </section>

        {post.cover_image && (
          <div className="bg-paper">
            <div className="max-w-[1100px] mx-auto px-6 md:px-12">
              <img src={post.cover_image} alt={post.title} className="w-full max-h-[80vh] object-contain bg-paper-2 -mt-12 relative z-10"/>
            </div>
          </div>
        )}

        <section className="bg-paper py-24 md:py-32">
          <div className="max-w-[820px] mx-auto px-6 md:px-12">
            <div className="prose-content text-[17px] md:text-[18px] leading-[1.8] text-ink/85 space-y-6">
              {post.content.split("\n\n").map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-brand text-white py-20 md:py-28 grain relative">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <p className="section-num text-white/85">{t("blog.furtherEyebrow")}</p>
              <h2 className="display text-3xl md:text-5xl leading-tight mt-6 max-w-3xl">
                {t("blog.furtherTitle")}
              </h2>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/contact" className="btn-pill bg-white text-brand hover:bg-paper-2">
                {t("common.cta.bookCall")} <ArrowRight size={16}/>
              </Link>
            </div>
          </div>
        </section>
      </article>
    </Layout>
  );
}
